import { ColumnDef } from '@tanstack/react-table'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { DealIcon } from '@/shared/components/icons/DealIcon'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { formatRequisitToType } from '@/features/requisites'
import { getBankMeta } from '@/shared/utils/getBankMeta'
import { getShortenId } from '@/shared/utils/getShortenId'
import { TopupsModel, TopupStatus, TopupStatusIcons, TopupStatusTitles } from '@/entities/topups'
import { TopupModel } from '@/entities/topup/model/types'
import type { TraderTopupProfile } from 'syncra-api-client'
import { BaseCopyButton } from '@/shared/components/BaseCopyButton/BaseCopyButton'

// Тип для данных топапов реферала, объединяющий разные структуры данных из API
export type ReferralTopupModel = (TopupsModel | TopupModel | TraderTopupProfile) & {
  requisitProfile?: {
    id: string
    bankToId: string
    bankToName: string
    recipientName: string
  }
  methodFrom?: string | null // Метод оплаты из API (например "TPay")
}

export const getReferralTopupsColumns = (): ColumnDef<ReferralTopupModel>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => {
      const id = row.getValue<string>('id') || row.original.id || ''
      return (
        <div className="flex items-center gap-1">
          <span className="font-medium text-primary">
            {getShortenId(id, 'long')}
          </span>
          <BaseCopyButton text={id} color="primary" size="mini" />
        </div>
      )
    },
  },
  {
    accessorKey: 'deal',
    header: 'Сделка',
    cell: ({ row }) => {
      const createdAt = row.original.createdAt || ''
      const status = row.original.status as TopupStatus
      const title = TopupStatusTitles[status as keyof typeof TopupStatusTitles]
      const iconStatus = TopupStatusIcons[status as keyof typeof TopupStatusIcons]

      return (
        <ItemLayout
          icon={<DealIcon status={iconStatus} />}
          subtitle={formatDate(createdAt)}
          title={title}
        />
      )
    },
  },
  {
    accessorKey: 'amount',
    header: 'Сумма',
    cell: ({ row }) => {
      const original = row.original
      // Проверяем наличие factAmountFrom/factAmountTo (есть в TopupsModel)
      const hasFactAmount = 'factAmountFrom' in original || 'factAmountTo' in original
      const amountFrom = (hasFactAmount && 'factAmountFrom' in original && original.factAmountFrom && original.factAmountFrom !== 0)
        ? original.factAmountFrom 
        : original.amountFrom
      const amountTo = (hasFactAmount && 'factAmountTo' in original && original.factAmountTo && original.factAmountTo !== 0)
        ? original.factAmountTo 
        : original.amountTo
      const currencyFrom = original.currencyFrom
      const currencyTo = original.currencyTo

      const formattedFrom = formatCurrency(amountFrom ?? 0, currencyFrom ?? 643)
      const formattedTo = formatCurrency(amountTo ?? 0, currencyTo ?? 643)

      return (
        <div className="flex flex-col">
          <span className="font-medium">{formattedTo}</span>
          <span className="text-text-secondary">{formattedFrom}</span>
        </div>
      )
    },
  },
  {
    accessorKey: 'method',
    header: 'Метод',
    cell: ({ row }) => {
      const original = row.original
      
      // Используем methodFrom из API ответа если есть
      if ('methodFrom' in original && original.methodFrom) {
        const methodFrom = original.methodFrom as string
        return <span className="font-medium">{methodFrom}</span>
      }
      
      return <span className="font-medium">-</span>
    },
  },
  {
    accessorKey: 'requisite',
    header: 'Реквизит',
    cell: ({ row }) => {
      const original = row.original
      let requisite = ''
      
      // Проверяем наличие paymentRequisitToValue/paymentRequisitToType (есть в TopupsModel)
      if ('paymentRequisitToValue' in original && 'paymentRequisitToType' in original) {
        const topupsModel = original as TopupsModel
        requisite = formatRequisitToType(
          topupsModel.paymentRequisitToType,
          topupsModel.paymentRequisitToValue
        )
      } 
      // Для TopupModel используем paymentRequisitProfile
      else if ('paymentRequisitProfile' in original && original.paymentRequisitProfile) {
        const topupModel = original as TopupModel
        requisite = formatRequisitToType(
          topupModel.paymentRequisitProfile.type,
          topupModel.paymentRequisitProfile.value
        )
      }
      
      // Получаем название банка из requisitProfile если есть
      const bankName = original.requisitProfile?.bankToName || ''
      const { title: bankTitle } = bankName ? getBankMeta(bankName) : { title: '' }

      return (
        <div className="flex flex-col">
          <span className="font-medium">{requisite || '—'}</span>
          {bankTitle && <span className="text-text-secondary">{bankTitle}</span>}
        </div>
      )
    },
  },
  {
    accessorKey: 'rate',
    header: 'Курс',
    cell: ({ row }) => {
      const rate = row.original.exchangeRate
      const time = formatDate(row.original.createdAt || '', 'timeOnly')

      return (
        <div className="flex flex-col">
          <span className="font-medium">{rate ? `${rate} RUB` : ''}</span>
          <span className="text-text-secondary">{rate && time ? `курс на ${time}` : ''}</span>
        </div>
      )
    },
  },
]

